import * as config from '@/config/config.json';
import * as calculatorConfig from '@/config/base-config.json';
import { isMobileScreenSize } from '@/utils/screen-size';
import {
  BOOKING_STAGE,
  CAR_PARK_STAGE,
  CEILING_STAGE,
  CSC_STAGE,
  FOUNDATION_STAGE,
  FRAMEWORK_STAGE,
  OTP_STAGE,
  TOP_STAGE,
  UTILITY_STAGE,
  WALL_STAGE,
  WINDOW_STAGE,
} from '../buc';
import { generateSliderSteps } from '@/utils/generateSliderSteps';

export const CI_PURCHASE_LABEL = {
  PurchaseType: 'Purchase Type',
  Type: 'Type',
  Resale: 'Resale',
  BUC: 'BUC',
  BUCLongForm: 'Building Under Construction',
  PurchaseDetails: 'Purchase Details',
  GST: 'GST',
  PurchasePrice: 'Purchase Price',
  ValuationPrice: 'Valuation Price',
  PotentialExpenses: 'Potential Expenses',
  OptionFee: 'Option Fee',
  OptionExerciseFee: 'Option Exercise Fee',
  BSD: 'BSD',
  BSDLongForm: "Buyer's Stamp Duty",
  LegalFee: 'Legal Fee',
  FinancingDetails: 'Financing Details',
  LoanTenure: 'Loan Tenure (Years)',
  MortgageLoanInterestRate: 'Mortgage Loan Interest Rate',
  LoanToValue: 'Loan-to-Value',
  MonthlyPayable: 'Monthly Payable',
  BreakdownOfLoan: 'Breakdown of Loan',
  Cash: 'Cash',
  LoanAmount: 'Loan Amount',
  Total: 'Total',
  Principal: 'Principal',
  Interest: 'Interest',
  MonthlyPayableNoteForResale: 'Estimation for the first payment month only upon completion',
  MonthlyPayableNoteForBUC: 'Estimation for the first payment month only upon CSC',
  FinancialBreakdown: 'Financial Breakdown',
  Items: 'Items',
  Monthly: 'Monthly',
  Loan: 'Loan',
  CashBreakdown: 'Cash Breakdown',
  COV: 'COV',
  COVLongForm: 'Cash Over Valuation',
  MandatoryCashTowardsValuation: 'Mandatory Cash Towards Valuation',
  TotalCashOutlay: 'Total Cash Outlay',
  GSTBreakdown: 'GST Breakdown',
  BasedOnPurchasePrice: 'Based on Purchase Price',
  PaidWithOptionAndExerciseFee: 'Paid with Option & Exercise Fee',
  GstPaidWithOptionFee: 'GST paid with Option Fee',
  OutstandingGSTPayable: 'Outstanding GST Payable',
  DownpaymentBreakdown: 'Downpayment Breakdown',
  DepositToBePaid: 'Deposit to be paid',
  OptionAndExerciseFee: 'Option & Exercise Fee',
  OutstandingPayablesInCash: 'Outstanding Payables in Cash',
  LessDepositIncludingCOV: 'Less Deposit, Including COV',
  LessDeposit: 'Less Deposit',
  PurchaseTimeline: 'Purchase Timeline',
  OTPLongForm: 'Option to Purchase',
  OTP: 'OTP',
  ExercisePeriod: 'Exercise Period',
  CompletionPeriod: 'Completion Period',
  CompletionOfFoundationWork: 'Completion of Foundation Work',
  Weeks: 'Weeks',
  Months: 'Months',
  SalesCompletion: 'Sales Completion',
  ExerciseOfOTP: 'Exercise of OTP',
  ReceiveOfSalesAndPurchase: 'Receive of Sales & Purchase',
  ExerciseOfSalesAndPurchase: 'Exercise of Sales & Purchase',
  ProgressiveDownpayment: 'Progressive Downpayment',
  GenerateTimeline: 'Generate Timeline',
};

export const LOAN_FINANCE_LABELS = [CI_PURCHASE_LABEL.Cash, CI_PURCHASE_LABEL.LoanAmount];
export const LOAN_FINANCE_COLORS = ['#F8CB59', '#F25A73'];

export const MONTHLY_PAYABLE_LABELS = [CI_PURCHASE_LABEL.Principal, CI_PURCHASE_LABEL.Interest];
export const MONTHLY_PAYABLE_COLORS = ['#9475EB', '#F25A73'];

export const LEGAL_FEES = {
  DEFAULT: calculatorConfig.legal_fee,
};

export const OPTION_FEE = {
  DEFAULT: calculatorConfig.option_fee_in_percent.default,
  RANGE: generateSliderSteps(0, calculatorConfig.option_fee_in_percent.max),
  MIN: calculatorConfig.option_fee_in_percent.min,
};

export const OPTION_EXERCISE_FEE = {
  DEFAULT: calculatorConfig.option_exercise_fee_in_percent.default,
  RANGE: generateSliderSteps(0, calculatorConfig.option_exercise_fee_in_percent.max),
  MIN: calculatorConfig.option_exercise_fee_in_percent.min,
};

export const CI_LOAN_TENURE = {
  LABEL_INPUTS: generateSliderSteps(0, config.max_loan_tenure_in_years.commercial_industrial, 10),
  MAX: config.max_loan_tenure_in_years.commercial_industrial,
  STEP: 1,
  DEFAULT: config.max_loan_tenure_in_years.commercial_industrial,
};

export const CI_MORTGAGE_INTEREST_RATE = {
  LABEL_INPUTS: generateSliderSteps(
    calculatorConfig.mortgage_interest_rate.commercial_industrial.min,
    calculatorConfig.mortgage_interest_rate.commercial_industrial.max,
    1,
  ),
  DEFAULT: calculatorConfig.mortgage_interest_rate.commercial_industrial.default,
  MAX: calculatorConfig.mortgage_interest_rate.commercial_industrial.max,
  STEP: 0.01,
};

export const CI_LOAN_TO_VALUE = {
  LABEL_INPUTS: generateSliderSteps(0, config.max_ltv_in_percent.commercial_industrial, 15),
  MAX: config.max_ltv_in_percent.commercial_industrial,
  STEP: 1,
  DEFAULT: config.max_ltv_in_percent.commercial_industrial,
};

export const EXERCISE_PERIOD = {
  DEFAULT: calculatorConfig.exercise_period_in_weeks.default,
  MIN: 1,
  MAX: calculatorConfig.exercise_period_in_weeks.max,
  STEP: 1,
  LABEL_INPUTS: generateSliderSteps(
    calculatorConfig.exercise_period_in_weeks.min,
    calculatorConfig.exercise_period_in_weeks.max,
  ),
};

export const COMPLETION_PERIOD = {
  DEFAULT: calculatorConfig.completion_period_in_weeks.default,
  MIN: calculatorConfig.completion_period_in_weeks.min,
  MAX: calculatorConfig.completion_period_in_weeks.max,
  STEP: 1,
  LABEL_INPUTS: generateSliderSteps(
    calculatorConfig.completion_period_in_weeks.min,
    calculatorConfig.completion_period_in_weeks.max,
  ),
};

export enum PurchaseTypes {
  Resale = 'resale',
  BUC = 'buc',
}

export enum GstStatus {
  YES = 'Yes',
  NO = 'No',
}

export enum FoundationPeriod {
  EIGHT_WEEKS = '0',
  THREE_SIX_MONTHS = '1',
  SIX_NINE_MONTHS = '2',
}

export const getPurchaseTypeLabel = (value: string) => {
  const isMobile = isMobileScreenSize();

  switch (value) {
    case PurchaseTypes.Resale: {
      return 'Resale';
    }

    case PurchaseTypes.BUC: {
      return isMobile ? 'BUC' : 'Building Under Construction';
    }

    default: {
      return '';
    }
  }
};

export const getCompletionFoundationLabel = (value: string) => {
  switch (value) {
    case FoundationPeriod.EIGHT_WEEKS: {
      return '8 Weeks';
    }
    case FoundationPeriod.THREE_SIX_MONTHS: {
      return '3-6 Months';
    }
    case FoundationPeriod.SIX_NINE_MONTHS: {
      return '6-9 Months';
    }

    default: {
      return '';
    }
  }
};

export const BUC_RECEIVE_OF_SP_DURATION = 2; // default 2 weeeks
export const BUC_EXERCISE_OF_SP_DURATION = 3; // default 3 weeeks
export const BUC_POTENTIAL_EXPENSES_DURATION = 2; // default 2 weeeks

// The stages must be the order
export const PurchaseBUCStage = [
  BOOKING_STAGE,
  OTP_STAGE,
  FOUNDATION_STAGE,
  FRAMEWORK_STAGE,
  WALL_STAGE,
  CEILING_STAGE,
  WINDOW_STAGE,
  UTILITY_STAGE,
  CAR_PARK_STAGE,
  TOP_STAGE,
  CSC_STAGE,
];

export const BUCStageCumulativePercentage = {
  BOOKING_STAGE: 100,
  OTP_STAGE: 95,
  FOUNDATION_STAGE: 80,
  FRAMEWORK_STAGE: 70,
  WALL_STAGE: 60,
  CEILING_STAGE: 55,
  WINDOW_STAGE: 50,
  UTILITY_STAGE: 45,
  CAR_PARK_STAGE: 40,
  TOP_STAGE: 35,
  CSC_STAGE: 10,
};
